import { Translate, TranslateHtml, TranslatePlural, TranslatePluralHtml } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Avatar } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { ParsedPromotion } from '../../types';

export function PromotionCard({ promotion }: { promotion: ParsedPromotion }) {
	const { amountFormatted, fulfilled, createdAtRelativeLocalized, stats, user } = promotion;
	return (
		<Card sx={{ borderColor: 'neutral-grey100', height: '100%', width: 200 }} p="16">
			{fulfilled ? (
				<Text size="caption" fontWeight="bold" sx={{ color: 'green' }}>
					<Translate value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.status.completed" />
				</Text>
			) : (
				<Text size="caption" fontWeight="bold" sx={{ color: 'purple' }}>
					<Translate value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.status.in_progress" />
				</Text>
			)}

			<Flex sx={{ flexWrap: 'nowrap' }} mt={8}>
				<Avatar alt="avatar" size="xs" variant="subtle" src={user.photo} />
				<Box ml="8px">
					<Text size="small" fontWeight="bold" ellipsis>
						{user.displayName}
					</Text>
					<Text size="caption" color="typography-secondary" ellipsis>
						{!amountFormatted ? createdAtRelativeLocalized : `${amountFormatted} • ${createdAtRelativeLocalized}`}
					</Text>
				</Box>
			</Flex>

			{stats && (
				<Box data-testid="promotion-stats" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
					<Text size="caption" color="typography-secondary" sx={{ '& b': { color: 'typography-primary' } }}>
						{fulfilled ? (
							<TranslatePluralHtml
								value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.completed_views"
								count={stats.impressionsTotal}
								replacements={{ impressionsTotal: stats.impressionsTotalLocalized }}
							/>
						) : (
							<>
								<VisuallyHidden>
									<TranslatePlural
										value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.in_progress_views_a11y"
										count={stats.impressionsServed}
										replacements={{
											impressionsServed: stats.impressionsServedLocalized,
											impressionsTotal: stats.impressionsTotalLocalized,
										}}
									/>
								</VisuallyHidden>
								<Text
									aria-hidden
									size="caption"
									color="typography-secondary"
									sx={{ '& b': { color: 'typography-primary' } }}
								>
									<TranslateHtml
										value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.in_progress_views"
										replacements={{
											impressionsServed: stats.impressionsServedLocalized,
											impressionsTotal: stats.impressionsTotalLocalized,
										}}
									/>
								</Text>
							</>
						)}
					</Text>
					<Flex sx={{ flexDirection: 'column', justifyContent: 'center' }}>
						<Text size="caption" fontWeight="bold">
							{stats.signatureCountLocalized}
						</Text>
						<Text size="caption" color="typography-secondary">
							<TranslatePlural
								value="corgi.petition.starter_dashboard.petition_insights.promotion_history.card.signatures"
								count={stats.signatureCount}
							/>
						</Text>
					</Flex>
				</Box>
			)}
		</Card>
	);
}
